import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import type { SyntheticEvent } from 'react'
import { Form } from 'semantic-ui-react'
import { FilterSchema, filters } from '@adc/blueprint'
import _ from 'lodash'
import DropdownField from '../../../formFields/dropdownField'
import RadioGroupField from '../../../formFields/radioGroupField'
import RadioField from '../../../formFields/radioField'
import SearchField from '../../../formFields/searchField'
import StandardFieldLabel from '../../../formFields/formFieldWrapper/standardFieldLabel'
import VirtualizedOptionList from './virtualizedOptionList'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

type SingleSelectProps = FilterSchema.FilterLayoutProps<
  FilterSchema.FilterSingleSelectDefinition,
  FilterSchema.SingleSelectValueType
>

const SingleSelect: React.FunctionComponent<SingleSelectProps> = ({
  compact,
  definition,
  value,
  elmId,
  onChange,
}) => {
  const searchFieldHasFocusRef = useRef(false)
  const previousCacheKeyRef = useRef<string | number | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [searchResults, setSearchResults] =
    useState<FilterSchema.SingleSelectOptions>([])
  const [loading, setLoading] = useState(false)
  const [asyncOptionsLoaded, setAsyncOptionsLoaded] = useState(false)

  const nonExistentFilterOption = useMemo(() => {
    const option = { ...filters.constants.nonExistentFilterOption }
    if (definition.nonExistentSearchLabel) {
      option.label = definition.nonExistentSearchLabel
    }
    return option
  }, [definition.nonExistentSearchLabel])

  const handleFocus = useCallback(() => {
    if (searchResults.length !== 0) {
      setOpen(true)
    }
    searchFieldHasFocusRef.current = true
  }, [searchResults])

  const handleBlur = useCallback(() => {
    setOpen(false)
    searchFieldHasFocusRef.current = false
  }, [])

  const staticOptions = useMemo(
    () =>
      definition.options.map(option => ({
        key: option.value,
        text: option.label,
        value: option.value,
      })),
    // asyncOptionsLoaded forces recomputation when async options are loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [definition.options, asyncOptionsLoaded]
  )

  useEffect(() => {
    if (definition.allowNonExistentSearch && searchResults.length === 0) {
      setSearchResults([nonExistentFilterOption])
    }
  }, [definition, searchResults.length, nonExistentFilterOption])

  // Fetch options once for asyncOnce mode on mount, or when cacheKey changes
  useEffect(() => {
    if (definition.lookupMode === 'asyncOnce' && definition.onSearchChange) {
      const cacheKeyChanged =
        definition.cacheKey !== previousCacheKeyRef.current
      const shouldFetch = definition.options.length === 0 || cacheKeyChanged

      if (shouldFetch) {
        // Clear cached options if cache key changed
        if (cacheKeyChanged && definition.options.length > 0) {
          definition.options = [] // Replace with empty array
        }

        previousCacheKeyRef.current = definition.cacheKey

        const fetchInitialOptions = async () => {
          setLoading(true)
          try {
            const results = await definition.onSearchChange!('')
            // Cache results in definition.options (matches existing async pattern)
            // Replace array reference to trigger staticOptions memo recomputation
            definition.options = [...results]
            // Trigger re-render by toggling state
            setAsyncOptionsLoaded(prev => !prev)
          } catch (error) {
            console.error(
              'Error fetching initial options for asyncOnce:',
              error
            )
          } finally {
            setLoading(false)
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchInitialOptions()
      }
    }
  }, [definition, definition.cacheKey])

  const handleSetSearchResults = useCallback(
    (results: FilterSchema.SingleSelectOptions) => {
      if (definition.allowNonExistentSearch) {
        results.unshift(nonExistentFilterOption)
      }
      setSearchResults(results)

      if (searchFieldHasFocusRef.current && !open) {
        setOpen(true)
      }
    },
    [definition.allowNonExistentSearch, open, nonExistentFilterOption]
  )

  const fetchResults = useCallback(
    async (searchTerm: string) => {
      if (
        definition.lookupMode === 'async' &&
        definition.onSearchChange &&
        typeof definition.onSearchChange === 'function'
      ) {
        // Check minimum search length
        if (
          definition.minSearchLength &&
          searchTerm.length < definition.minSearchLength
        ) {
          handleSetSearchResults([])
          return
        }

        setLoading(true)
        try {
          const results = await definition.onSearchChange(searchTerm)
          handleSetSearchResults(results)
        } catch (error) {
          console.error('Error fetching search results:', error)
          handleSetSearchResults([])
        }
        setLoading(false)
      }
    },
    [definition, handleSetSearchResults]
  )

  const debouncedFetch = useRef(
    _.debounce(fetchResults, definition.searchDebounceMs || 500)
  ).current

  /**
   * Opt-in typeahead for the static dropdown branch. When the definition
   * carries a typeahead config, enable semantic-ui's search input with a
   * label-prefix filter and (optionally) numeric-only key handling. When
   * absent, the dropdown stays non-searchable — identical to the previous
   * behavior.
   */
  const typeaheadProps = useMemo(() => {
    const typeahead = definition.typeahead
    if (!typeahead) return { search: false as const }

    return {
      search: (
        options: Array<{ text?: React.ReactNode }>,
        query: string
      ): Array<{ text?: React.ReactNode }> =>
        options.filter(option => {
          const label =
            typeof option.text === 'string' || typeof option.text === 'number'
              ? String(option.text)
              : ''
          return label.toLowerCase().startsWith(query.toLowerCase())
        }),
      searchInput: {
        ...(typeahead.numericOnly ? { inputMode: 'numeric' as const } : {}),
        ...(typeahead.maxLength ? { maxLength: typeahead.maxLength } : {}),
        ...(typeahead.numericOnly
          ? {
              onKeyPress: (event: React.KeyboardEvent) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault()
                }
              },
            }
          : {}),
      },
    }
  }, [definition.typeahead])

  const onSearchChange = useCallback(
    (_e: SyntheticEvent, { value: searchTerm }: { value: string }) => {
      if (definition.lookupMode === 'async') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        debouncedFetch(searchTerm)
      }
    },
    [definition.lookupMode, debouncedFetch]
  )

  const searchFieldResults = useMemo(() => {
    const options =
      definition.lookupMode === 'async' ? searchResults : definition.options
    return options.map(option => ({
      title: option.label,
      description: option.description,
      value: option.value,
    }))
  }, [definition.lookupMode, definition.options, searchResults])

  // Find the selected item label for SearchField
  const selectedLabel = useMemo(() => {
    if (!value) return ''
    // In async mode, check both searchResults and static options
    // This handles cases where a value is pre-selected
    const allOptions =
      definition.lookupMode === 'async'
        ? [...searchResults, ...definition.options]
        : definition.options
    const selected = allOptions.find(option => option.value === value)
    return selected?.label || ''
  }, [value, definition.lookupMode, definition.options, searchResults])

  // Fetch and cache label for async filters on mount if value exists
  useEffect(() => {
    if (
      value &&
      definition.lookupMode === 'async' &&
      definition.getOptionByValue &&
      !definition.options.some(opt => opt.value === value)
    ) {
      definition
        .getOptionByValue(value)
        .then(option => {
          if (option && !definition.options.some(opt => opt.value === value)) {
            // eslint-disable-line
            // Add to options array to cache it
            definition.options.push(option)
            // Force re-render by updating search results
            setSearchResults(prev => [...prev])
          }
        })
        .catch(error => {
          console.error('Error fetching option by value:', error)
        })
    }
  }, [value, definition])

  // Static mode with localFilterable — use shared VirtualizedOptionList with radio items
  const isStaticLocalFilterable =
    definition.localFilterable &&
    (!definition.lookupMode || definition.lookupMode === 'static') &&
    definition.styleIntent?.controlVariant !== 'inline-radio'

  if (isStaticLocalFilterable) {
    return (
      <VirtualizedOptionList
        options={definition.options}
        localFilterable={definition.localFilterable}
        localFilterPlaceholder={definition.localFilterPlaceholder}
        localFilterMinLength={definition.localFilterMinLength}
        localFilterMatchMode={definition.localFilterMatchMode}
        elmId={elmId}
        containerClassName={cx('multi-select-container')}
        renderItem={(option, index) => (
          <RadioField
            className={cx('no-margin-checkbox')}
            compact={compact}
            elmId={`${elmId}_${_.snakeCase(String(option.value))}`}
            checked={value === option.value}
            value={option.value ?? ''}
            onChange={() => {
              onChange(definition.name, option.value)
            }}
            tabIndex={index}
            label={
              <StandardFieldLabel
                elmId={`${elmId}_${_.snakeCase(String(option.value))}_label`}
                label={option.label}
                subLabel={(option.description as string) || ''}
              />
            }
          />
        )}
        getRowTitle={option => {
          if (typeof option.description === 'string')
            return `${option.label} — ${option.description}`
          return typeof option.label === 'string' ? option.label : undefined
        }}
      />
    )
  }

  return (
    <Form>
      {/* eslint-disable-next-line no-nested-ternary */}
      {definition.styleIntent?.controlVariant === 'inline-radio' ? (
        <RadioGroupField
          name={definition.name}
          label=''
          elmId={`${elmId}_${definition.name}`}
          options={definition.options}
          value={value || ''} // underlying component doesn't like null
          onChange={(
            _e: never, // eslint-disable-line @typescript-eslint/no-unused-vars
            input: { value: FilterSchema.SingleSelectControlValueType }
          ) => {
            onChange(definition.name, input.value || null)
          }}
          hideWrapper
        />
      ) : definition.lookupMode === 'async' ? (
        <SearchField
          compact={compact}
          elmId={`${elmId}_${definition.name}`}
          placeholder={definition.placeholder || 'Search...'}
          value={selectedLabel}
          results={searchFieldResults}
          loading={loading}
          onSearchChange={onSearchChange}
          onResultSelect={(
            _e: SyntheticEvent,
            {
              result,
            }: { result: { value: FilterSchema.SingleSelectControlValueType } }
          ) => {
            onChange(definition.name, result.value)
          }}
          minCharacters={definition.minSearchLength || 0}
          hideWrapper
          fluid
          open={open}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      ) : definition.lookupMode === 'asyncOnce' ? (
        <DropdownField
          compact={compact}
          name={definition.name}
          placeholder={definition.placeholder || 'Select'}
          label=''
          elmId={`${elmId}_${definition.name}`}
          options={staticOptions}
          value={value}
          onChange={(newValue: FilterSchema.SingleSelectValueType) => {
            onChange(definition.name, newValue)
          }}
          loading={loading}
          search={false}
          wrapSelection={false}
          fluid
          hideWrapper
        />
      ) : (
        <DropdownField
          compact={compact}
          name={definition.name}
          placeholder={definition.placeholder || 'Select'}
          label=''
          elmId={`${elmId}_${definition.name}`}
          options={staticOptions}
          value={value}
          onChange={(newValue: FilterSchema.SingleSelectValueType) => {
            onChange(definition.name, newValue)
          }}
          wrapSelection={false}
          fluid
          hideWrapper
          {...typeaheadProps}
        />
      )}
    </Form>
  )
}

export default SingleSelect
